import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiChevronDown,
  FiChevronRight,
  FiClock,
  FiLoader,
  FiSearch,
  FiTrash2,
} from 'react-icons/fi';
import { MdOutlineHistory } from 'react-icons/md';

import {
  getQuickInferenceSession,
  type QuickInferenceSessionListItem,
  type QuickInferenceSessionStatusFilter,
} from '../../../helpers/api/quick-inference/quick-inference';
import {
  fileKindFromMeta,
  formatRelativeTime,
  sessionDateGroup,
  sessionDisplayTitle,
  sessionLabel,
  sessionStatusLabel,
  shortSessionId,
  truncateFilename,
} from './quickInferenceUtils';
import { QuickInferenceMediaThumb } from './QuickInferenceMediaThumb';

interface Props {
  items: QuickInferenceSessionListItem[];
  total: number;
  statusCounts: {
    all: number;
    active: number;
    success: number;
    failed: number;
  };
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  filter: QuickInferenceSessionStatusFilter;
  search: string;
  onFilterChange: (filter: QuickInferenceSessionStatusFilter) => void;
  onSearchChange: (query: string) => void;
  onLoadMore: () => void;
  onOpen: (sessionId: string) => void;
  onDelete: (sessionId: string) => void;
}

const PAGE_HINT_SIZE = 20;

const FILTERS: { id: QuickInferenceSessionStatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Running' },
  { id: 'success', label: 'Done' },
  { id: 'failed', label: 'Failed' },
];

const STATUS_DOT: Record<string, string> = {
  success: 'bg-emerald-500',
  fail: 'bg-red-500',
  partial: 'bg-amber-500',
  processing: 'bg-blue-500',
  pending: 'bg-slate-400',
};

function SessionThumb({ session }: { session: QuickInferenceSessionListItem }) {
  const files = session.files ?? [];
  const first = files[0];
  const kind = first
    ? fileKindFromMeta(first.content_type, first.filename, first.pacs_ingest_status)
    : 'image';
  const isActive = session.status === 'pending' || session.status === 'processing';

  return (
    <div
      className={`relative h-11 w-11 shrink-0 overflow-hidden rounded-xl ${
        isActive
          ? 'ring-2 ring-blue-100 dark:ring-blue-800'
          : 'ring-1 ring-slate-200 dark:ring-gray-600'
      }`}
    >
      <QuickInferenceMediaThumb
        inferenceId={first?.id}
        status={first?.status ?? (isActive ? 'pending' : 'success')}
        kind={kind}
        iconSize={14}
        className="h-full w-full"
      />
      {session.file_count > 1 ? (
        <span className="absolute -bottom-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-slate-800 px-1 text-[9px] font-bold text-white">
          {session.file_count}
        </span>
      ) : null}
      {isActive ? (
        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-blue-500 dark:border-gray-800" />
      ) : null}
    </div>
  );
}

function SessionRow({
  session,
  onOpen,
  onDelete,
}: {
  session: QuickInferenceSessionListItem;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [loadedFiles, setLoadedFiles] = useState(session.files ?? []);
  const isActive = session.status === 'pending' || session.status === 'processing';
  const dotClass = STATUS_DOT[session.status] ?? STATUS_DOT.pending;
  const hasAllFiles = loadedFiles.length >= session.file_count && session.file_count > 0;

  useEffect(() => {
    setLoadedFiles(session.files ?? []);
  }, [session.id, session.files]);

  const handleToggleFiles = useCallback(async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }

    setExpanded(true);
    if (hasAllFiles || session.file_count === 0) return;

    setFilesLoading(true);
    try {
      const detail = await getQuickInferenceSession(session.id);
      if (detail?.files?.length) {
        setLoadedFiles(detail.files);
      }
    } finally {
      setFilesLoading(false);
    }
  }, [expanded, hasAllFiles, session.file_count, session.id]);

  return (
    <li className="group">
      <div
        className={`relative rounded-xl border transition-all ${
          isActive
            ? 'border-blue-200 bg-blue-50/40 shadow-sm dark:border-blue-800 dark:bg-blue-900/10'
            : 'border-transparent bg-white hover:border-slate-200 hover:bg-slate-50/80 hover:shadow-sm dark:bg-transparent dark:hover:border-gray-600 dark:hover:bg-gray-800/50'
        }`}
      >
        <div className="flex gap-3 p-3">
          <button type="button" onClick={() => onOpen(session.id)} className="shrink-0">
            <SessionThumb session={{ ...session, files: loadedFiles }} />
          </button>

          <div className="min-w-0 flex-1">
            <button type="button" onClick={() => onOpen(session.id)} className="w-full text-left">
              <div className="flex items-start justify-between gap-2">
                <p className="truncate text-sm font-semibold leading-tight text-slate-800 dark:text-gray-100">
                  {sessionDisplayTitle({ ...session, files: loadedFiles })}
                </p>
                <span className="shrink-0 text-[10px] text-slate-400" title={session.created_at ?? ''}>
                  {formatRelativeTime(session.created_at)}
                </span>
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1">
                  <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
                  {isActive ? (
                    <FiLoader size={10} className="animate-spin text-blue-500" />
                  ) : null}
                  {sessionStatusLabel(session.status)}
                </span>
                <span className="text-slate-300">·</span>
                <span>
                  {session.file_count} file{session.file_count === 1 ? '' : 's'}
                </span>
                <span className="text-slate-300">·</span>
                <span>{session.total_frames} frames</span>
              </div>
            </button>

            {session.file_count > 0 ? (
              <button
                type="button"
                onClick={() => void handleToggleFiles()}
                className="mt-1.5 inline-flex items-center gap-0.5 text-[10px] font-medium text-slate-400 transition hover:text-blue-600"
              >
                {expanded ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                {expanded ? 'Hide files' : 'Show files'}
                {filesLoading ? <FiLoader size={10} className="animate-spin" /> : null}
              </button>
            ) : null}

            {expanded && loadedFiles.length > 0 ? (
              <ul className="mt-2 space-y-1 rounded-lg bg-slate-50 px-2 py-1.5 dark:bg-gray-900/50">
                {loadedFiles.map((file) => {
                  const kind = fileKindFromMeta(
                    file.content_type,
                    file.filename,
                    file.pacs_ingest_status
                  );
                  return (
                    <li
                      key={file.id}
                      className="flex items-center gap-1.5 text-[10px] text-slate-600 dark:text-gray-400"
                    >
                      <div className="h-6 w-6 shrink-0 overflow-hidden rounded">
                        <QuickInferenceMediaThumb
                          inferenceId={file.id}
                          status={file.status}
                          kind={kind}
                          iconSize={10}
                          className="h-full w-full"
                        />
                      </div>
                      <span className="truncate" title={file.filename}>
                        {truncateFilename(file.filename, 32)}
                      </span>
                    </li>
                  );
                })}
                <li className="pt-0.5 font-mono text-[9px] text-slate-400" title={sessionLabel(session)}>
                  ID {shortSessionId(session.id)}
                </li>
              </ul>
            ) : null}
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(session.id);
            }}
            className="h-8 w-8 shrink-0 self-start rounded-lg text-slate-400 opacity-100 transition hover:bg-red-50 hover:text-red-500 sm:opacity-0 sm:group-hover:opacity-100 dark:hover:bg-red-900/20"
            aria-label="Delete session"
          >
            <FiTrash2 size={15} className="mx-auto" />
          </button>
        </div>
      </div>
    </li>
  );
}

export function QuickInferenceSessionSidebar({
  items,
  total,
  statusCounts,
  loading,
  loadingMore,
  hasMore,
  filter,
  search,
  onFilterChange,
  onSearchChange,
  onLoadMore,
  onOpen,
  onDelete,
}: Props) {
  const groupedItems = useMemo(() => {
    const order = ['Today', 'Yesterday', 'This week', 'Older'];
    const map = new Map<string, QuickInferenceSessionListItem[]>();
    items.forEach((session) => {
      const group = sessionDateGroup(session.created_at);
      if (!map.has(group)) map.set(group, []);
      map.get(group)!.push(session);
    });
    return order
      .filter((g) => map.has(g))
      .map((label) => ({ label, sessions: map.get(label)! }));
  }, [items]);

  const showEmptyHistory = !loading && total === 0 && !search && filter === 'all';
  const showNoMatches = !loading && items.length === 0 && !showEmptyHistory;

  return (
    <div className="flex h-full min-h-0 flex-col bg-gradient-to-b from-slate-50/80 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="shrink-0 border-b border-slate-100 px-4 pb-3 pt-4 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-700 text-white shadow-md shadow-slate-700/20 dark:bg-slate-600">
              <MdOutlineHistory size={18} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">History</h3>
              <p className="text-[11px] text-slate-500">
                {loading && items.length === 0
                  ? 'Loading…'
                  : `${total} session${total === 1 ? '' : 's'}`}
              </p>
            </div>
          </div>
        </div>

        {total > 0 || search || filter !== 'all' ? (
          <>
            <div className="relative mt-3">
              <FiSearch
                size={13}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search by filename or ID…"
                className="w-full rounded-xl border-0 bg-white py-2 pl-9 pr-3 text-xs text-slate-700 shadow-sm ring-1 ring-slate-200 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500/30 dark:bg-gray-900 dark:text-gray-200 dark:ring-gray-600"
              />
            </div>

            <div className="mt-2.5 flex gap-1 overflow-x-auto pb-0.5">
              {FILTERS.map((opt) => {
                const count = statusCounts[opt.id];
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => onFilterChange(opt.id)}
                    className={`shrink-0 rounded-lg px-2.5 py-1 text-[10px] font-semibold transition ${
                      filter === opt.id
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-white text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-600'
                    }`}
                  >
                    {opt.label}
                    {count > 0 ? ` (${count})` : ''}
                  </button>
                );
              })}
            </div>
          </>
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 custom-scrollbar">
        {loading && items.length === 0 ? (
          <div className="space-y-2 px-1">
            {[1, 2, 4].map((i) => (
              <div key={i} className="flex gap-3 rounded-xl p-3">
                <div className="h-11 w-11 animate-pulse rounded-xl bg-slate-200 dark:bg-gray-700" />
                <div className="flex-1 space-y-2 py-1">
                  <div className="h-3 w-3/4 animate-pulse rounded bg-slate-200 dark:bg-gray-700" />
                  <div className="h-2 w-1/2 animate-pulse rounded bg-slate-100 dark:bg-gray-800" />
                </div>
              </div>
            ))}
          </div>
        ) : showEmptyHistory ? (
          <div className="mx-1 flex min-h-[240px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white/60 px-5 py-10 text-center dark:border-gray-600 dark:bg-gray-900/20">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-gray-800">
              <FiClock size={22} />
            </div>
            <p className="text-sm font-semibold text-slate-700 dark:text-gray-200">No history yet</p>
            <p className="mt-1.5 max-w-[210px] text-xs leading-relaxed text-slate-400">
              Completed inference sessions will show up here for quick access
            </p>
          </div>
        ) : showNoMatches ? (
          <div className="py-16 text-center">
            <p className="text-xs font-medium text-slate-500">No matches</p>
            <p className="mt-1 text-[11px] text-slate-400">Try a different search or filter</p>
          </div>
        ) : (
          <div className="space-y-4">
            {groupedItems.map(({ label, sessions }) => (
              <section key={label}>
                <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {label}
                </p>
                <ul className="space-y-1">
                  {sessions.map((session) => (
                    <SessionRow
                      key={session.id}
                      session={session}
                      onOpen={onOpen}
                      onDelete={onDelete}
                    />
                  ))}
                </ul>
              </section>
            ))}

            {hasMore ? (
              <div className="px-1 pb-2 pt-1">
                <button
                  type="button"
                  onClick={onLoadMore}
                  disabled={loadingMore}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-2.5 text-xs font-semibold text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-800 dark:hover:bg-blue-900/20"
                >
                  {loadingMore ? (
                    <>
                      <FiLoader size={14} className="animate-spin" />
                      Loading…
                    </>
                  ) : (
                    <>Load more ({items.length} of {total})</>
                  )}
                </button>
              </div>
            ) : items.length > 0 && total > PAGE_HINT_SIZE ? (
              <p className="px-1 pb-2 text-center text-[10px] text-slate-400">
                Showing all {total} sessions
              </p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
